// Mitigación de "stale chunk" (ficha bugs/2026-07-19-cf-cache-html-assets-404):
// Cloudflare cachea el HTML anónimo de `/` y `/mastermind-pro`; tras un deploy
// ese HTML rancio referencia chunks con hash de un build viejo que puede no
// existir ya (evidencia real: MastermindProLanding-CEQ2PL07.js → 404) y el
// import dinámico falla dejando la página rota. La recuperación clásica es
// recargar la página UNA sola vez: la recarga pide el HTML de nuevo y, tras la
// purga del deploy, llega el fresco. El guard en sessionStorage evita un bucle
// de recargas si el HTML sigue rancio tras recargar: el segundo fallo se deja
// aflorar en vez de recargar otra vez.

export const STALE_CHUNK_RELOAD_KEY = "mm:stale-chunk-reload";

export interface StaleChunkDeps {
  storage?: Storage;
  reload?: () => void;
}

// Devuelve true si ha lanzado la recarga (única) de recuperación; false si el
// guard ya estaba puesto o si no hay sessionStorage utilizable (sin guard no
// recargamos: un reload incondicional sí podría entrar en bucle).
export function reloadOnceForStaleChunk(deps: StaleChunkDeps = {}): boolean {
  const storage = deps.storage ?? window.sessionStorage;
  const reload = deps.reload ?? (() => window.location.reload());

  try {
    if (storage.getItem(STALE_CHUNK_RELOAD_KEY) !== null) return false;
    storage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }

  reload();
  return true;
}

// Se llama cuando una navegación carga su chunk con éxito: el auto-reload
// anterior (si lo hubo) funcionó, así que un futuro deploy puede volver a
// auto-recuperarse. Nunca se limpia entre un reload fallido y el siguiente
// fallo, porque sin navegación con éxito no hay señal de recuperación.
export function clearStaleChunkReloadGuard(
  storage: Storage = window.sessionStorage,
): void {
  try {
    storage.removeItem(STALE_CHUNK_RELOAD_KEY);
  } catch {
    // Sin storage no hay guard que limpiar.
  }
}

// Vite emite "vite:preloadError" en window cuando falla la carga de un import
// dinámico o de sus dependencias precargadas (caso típico: chunk de un build
// viejo). preventDefault() evita que Vite relance el error mientras la recarga
// está en vuelo; si el guard impide recargar, el error aflora con normalidad.
export function installStaleChunkReload(
  target: Window = window,
  deps: StaleChunkDeps = {},
): void {
  target.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForStaleChunk(deps)) {
      event.preventDefault();
    }
  });
}
